import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, Code, Layers, LineChart, Share2, Users, BarChart, Blocks, Database, Users2, CreditCard, Briefcase } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import BusinessInfoBoxes from '@/components/BusinessInfoBoxes';
const NoaOperatingPlatform: React.FC = () => {
  const {
    language
  } = useLanguage();

  // Updated with shorter titles for the growth levers section
  const growthLeversTitles = ["Revenue Management", "Product Innovation", "Technology, Data & AI", "Financial Operations", "People & Culture", "Strategic M&A"];
  return <div className="min-h-screen bg-noa-antiflash-white print:bg-white">
      {/* Navigation only visible on screen, hidden when printing */}
      <div className="print:hidden">
        <Navbar />
      </div>
      
      <main className="print:p-0 print:w-[210mm] print:h-[297mm] print:max-w-[210mm] print:max-h-[297mm] print:overflow-hidden print:mx-auto">
        {/* Hero Section - Added more top padding */}
        <section className="relative bg-noa-rich-black text-white py-12 md:py-16 pt-20 md:pt-24 print:py-6">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-10 print:mb-4">
              <h2 className="font-space-grotesk text-3xl sm:text-4xl lg:text-5xl print:text-2xl font-bold mb-4">Platform & Operating Model</h2>
            </div>
          </div>
        </section>
        
        {/* Our Operating Model Section */}
        <section className="py-8 md:py-12 print:py-4 bg-white print:mt-2">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-8 print:mb-2">
              <h2 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1">Our Operating Principles</h2>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto">Empowering operators to lead—through trust, flexibility, and aligned outcomes.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3 print:gap-2">
              <Card className="border-0 shadow-md print:shadow-none print:border print:border-gray-200">
                <CardHeader className="pb-2 print:py-1 print:px-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 mb-2 print:w-6 print:h-6 print:mb-1">
                    <Users className="h-5 w-5 text-blue-600 print:h-3 print:w-3" />
                  </div>
                  <CardTitle className="text-lg print:text-sm">Back Long-Term Builders</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 print:py-1 print:px-2">
                  <p className="text-noa-paynes-gray text-sm print:text-xs">
                    Flexible transition plans for founders who want to stay, step back, or hand over leadership.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-md print:shadow-none print:border print:border-gray-200">
                <CardHeader className="pb-2 print:py-1 print:px-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 mb-2 print:w-6 print:h-6 print:mb-1">
                    <Share2 className="h-5 w-5 text-blue-600 print:h-3 print:w-3" />
                  </div>
                  <CardTitle className="text-lg print:text-sm">Decentralized Management</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 print:py-1 print:px-2">
                  <p className="text-noa-paynes-gray text-sm print:text-xs">Trusted operators lead day-to-day, supported by Noa's playbooks, resources, and shared infrastructure.</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-md print:shadow-none print:border print:border-gray-200">
                <CardHeader className="pb-2 print:py-1 print:px-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 mb-2 print:w-6 print:h-6 print:mb-1">
                    <Layers className="h-5 w-5 text-blue-600 print:h-3 print:w-3" />
                  </div>
                  <CardTitle className="text-lg print:text-sm">Aligned Incentives</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 print:py-1 print:px-2">
                  <p className="text-noa-paynes-gray text-sm print:text-xs">Tied to earnouts, growth milestones, and long-term equity for operators who stay and scale.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        
        {/* A Platform of Platforms */}
        <section className="py-8 md:py-12 print:py-4 bg-noa-antiflash-white print:bg-gray-50 print:mt-2">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-8 print:mb-2">
              <h2 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1">Building a Platform of Platforms</h2>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto">Acquiring market-leading VMS in essential industries—with tailwinds and strong growth opportunities.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3 print:gap-2">
              <Card className="border-0 shadow-md print:shadow-none print:border print:border-gray-200">
                <CardHeader className="pb-2 print:py-1 print:px-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 mb-2 print:w-6 print:h-6 print:mb-1">
                    <LineChart className="h-5 w-5 text-blue-600 print:h-3 print:w-3" />
                  </div>
                  <CardTitle className="text-lg print:text-sm">Industry-Led Thesis</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 print:py-1 print:px-2">
                  <p className="text-noa-paynes-gray text-sm print:text-xs">
                    We acquire market leaders in essential industries that serve as future platform companies.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-md print:shadow-none print:border print:border-gray-200">
                <CardHeader className="pb-2 print:py-1 print:px-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 mb-2 print:w-6 print:h-6 print:mb-1">
                    <Book className="h-5 w-5 text-blue-600 print:h-3 print:w-3" />
                  </div>
                  <CardTitle className="text-lg print:text-sm">Tailwinds & Consolidation</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 print:py-1 print:px-2">
                  <p className="text-noa-paynes-gray text-sm print:text-xs">
                    Focused on verticals undergoing tech transformation with roll-up potential.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-md print:shadow-none print:border print:border-gray-200">
                <CardHeader className="pb-2 print:py-1 print:px-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 mb-2 print:w-6 print:h-6 print:mb-1">
                    <Code className="h-5 w-5 text-blue-600 print:h-3 print:w-3" />
                  </div>
                  <CardTitle className="text-lg print:text-sm">Essential Industries Focus</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 print:py-1 print:px-2">
                  <p className="text-noa-paynes-gray text-sm print:text-xs">
                    Automotive, Construction, Healthcare, Education, Logistics, and other sectors.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        
        {/* Shared Services Platform - Updated to Six Strategic Pillars with updated text */}
        <section className="py-8 md:py-12 print:py-4 bg-white print:mt-2">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-8 print:mb-2">
              <h2 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1">Operating Playbook: 6 Pillars of Value</h2>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto">Core areas we underwrite and drive value—executed alongside the right long-term operating partner.</p>
            </div>
            
            <BusinessInfoBoxes className="mb-16 md:mb-24 print:mb-4" />
          </div>
        </section>
        
        {/* Noa Partners - Our Value Proposition - Updated with Hero Section */}
        <section className="relative bg-noa-rich-black text-white py-12 md:py-16 print:py-6 print:mt-2">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-10 print:mb-4">
              <h2 className="font-space-grotesk text-3xl sm:text-4xl lg:text-5xl print:text-2xl font-bold mb-4">Noa Partners - Value Proposition</h2>
            </div>
          </div>
        </section>
        
        {/* Why Owners Choose Noa Partners - Now a separate section after the hero */}
        <section className="py-8 md:py-12 print:py-4 bg-white print:mt-2">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="mb-16">
              <h3 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1 text-center">Why Owners Choose Noa Partners</h3>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto text-center mb-8">Long-term capital, real support, and the freedom to keep leading.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 print:grid-cols-3 print:gap-4">
                <Card className="border border-gray-200 hover:border-blue-500 transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-50 mb-4">
                      <Briefcase className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">Buy, Hold & Grow Long-Term</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-noa-paynes-gray">We offer a permanent home—not a flip. Founders trust us to preserve their legacy while driving long-term growth.</p>
                  </CardContent>
                </Card>
                
                <Card className="border border-gray-200 hover:border-blue-500 transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-50 mb-4">
                      <LineChart className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">Strategic Support to Scale</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-noa-paynes-gray">We provide more than capital—access to playbooks, talent, and shared resources to accelerate growth.</p>
                  </CardContent>
                </Card>
                
                <Card className="border border-gray-200 hover:border-blue-500 transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-50 mb-4">
                      <Share2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">Decentralized by Design</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-noa-paynes-gray">Whether the owner stays or steps back, operators lead day-to-day—backed by our strategic support and guidance.
                  </p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Filling a Buyer Gap in the Market - Updated Design */}
            <div className="mb-16">
              <h3 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1 text-center">Filling a Buyer Gap in the Market</h3>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto text-center mb-8">We sit between models that don't fit—offering local trust, operational depth, and long-term commitment.</p>
              
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100 shadow-sm mb-8">
                <div className="max-w-4xl mx-auto">
                  
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-400">
                      <h4 className="font-bold text-gray-800 mb-2">Private Equity</h4>
                      <p className="text-gray-600">Too focused on short-term returns, heavy financial engineering, and control.</p>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-400">
                      <h4 className="font-bold text-gray-800 mb-2">Search Funds</h4>
                      <p className="text-gray-600">Often lack operational depth and permanent capital—limited in scope and scale.</p>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-400">
                      <h4 className="font-bold text-gray-800 mb-2">Global Serial Acquirers</h4>
                      <p className="text-gray-600">Lack local presence, cultural alignment, and fail to earn seller trust.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Why Local Matters - Updated Design */}
            <div className="mb-16">
              <h3 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1 text-center">Why Local Matters</h3>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto text-center mb-8">Founders don't just sell on price—they sell to people they trust.</p>
              
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100 shadow-sm">
                <div className="max-w-4xl mx-auto">
                  <div className="text-center mb-8">
                    
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-sm border-t-4 border-blue-500">
                      <h4 className="font-bold text-gray-800 mb-2">On-the-ground presence</h4>
                      <p className="text-gray-600">Builds credibility and unlocks proprietary deal flow.</p>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-sm border-t-4 border-blue-500">
                      <h4 className="font-bold text-gray-800 mb-2">Cultural fluency</h4>
                      <p className="text-gray-600">Ensures smoother transitions and stronger post-close retention.</p>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-sm border-t-4 border-blue-500">
                      <h4 className="font-bold text-gray-800 mb-2">Faster execution</h4>
                      <p className="text-gray-600">Through local networks, relationships, and real-time decision-making.</p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 text-center">
                    <p className="text-lg font-bold text-gray-800">Noa Partners fills the gap with permanent capital, local execution, and deep operational experience—built in and for Latin America.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Identified growth levers - Updated with detailed info boxes */}
            <div className="mb-16">
              <h3 className="font-space-grotesk text-2xl sm:text-3xl print:text-xl font-bold mb-2 print:mb-1 text-center">Identified growth levers during DD</h3>
              <p className="text-base print:text-xs text-noa-paynes-gray max-w-3xl mx-auto text-center mb-8">To be validated and stress-tested during the earnout period, with additional opportunities explored post-close.
            </p>
              
              <BusinessInfoBoxes customTitles={growthLeversTitles} detailedContent={true} className="max-w-6xl mx-auto" />
              
              <div className="bg-green-50 p-6 rounded-lg border border-green-100 text-center mt-8 max-w-4xl mx-auto">
                <p className="text-lg font-bold text-gray-800">Our playbook has consistently delivered 15-30% ARR growth within the first 24 months post-acquisition.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section - Only visible on screen, hidden when printing */}
        <section className="py-8 md:py-12 bg-noa-rich-black text-white print:hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center">
              <h2 className="font-space-grotesk text-2xl sm:text-3xl font-bold mb-6">
                Ready to join the Noa family?
              </h2>
              <Link to="/contact">
                <Button className="px-6 py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-lg flex items-center gap-2 mx-auto">
                  Get in Touch
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer only visible on screen, hidden when printing */}
      <div className="print:hidden">
        <Footer />
      </div>
      
      {/* Print styles - This invisible element provides CSS rules when printing */}
      <style dangerouslySetInnerHTML={{
      __html: `
        @media print {
          @page {
            size: 210mm 297mm portrait;
            margin: 0;
          }
          body {
            width: 210mm;
            height: 297mm;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `
    }} />
    </div>;
};
export default NoaOperatingPlatform;
